import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Link,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ClosedFamilyMemberRef } from '@/api/DTO/family/family.interface';
import ClosedFamilyMemberPickerDialog from './ClosedFamilyMemberPickerDialog';

interface Props {
	value: ClosedFamilyMemberRef | null;
	onChange: (value: ClosedFamilyMemberRef | null) => void;
}

/**
 * DMS-REQ-WEB-CHA-004 §二: new person is the default; a closed family member
 * of this centre is picked from a separate entry (§四).
 */
export default function PreviousFamilyMemberBanner({ value, onChange }: Props) {
	const { t } = useTranslation();
	const [pickerOpen, setPickerOpen] = useState(false);

	return (
		<>
			{value ? (
				<Alert
					severity='info'
					sx={{ marginTop: 2, bgcolor: '#D3E9F8' }}
					action={
						<Button
							variant='outlined'
							size='small'
							sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
							onClick={() => onChange(null)}
						>
							{t('memberDuplication.removeSelection')}
						</Button>
					}
				>
					<AlertTitle>
						{t('memberDuplication.previousFamilyMemberSelectedTitle')}
					</AlertTitle>
					<Trans
						i18nKey='memberDuplication.previousFamilyMemberSelected'
						values={{
							fileNo: value.familyMemberFileNo,
							name: [value.nameEn, value.nameZh].filter(Boolean).join(' '),
						}}
						components={{
							anchor: (
								<Link
									href={`/family-management/family-management-list/detail/${value.id}`}
									target='_blank'
									rel='noopener noreferrer'
									color='#1BBED7'
								/>
							),
						}}
					/>
				</Alert>
			) : (
				<Box
					sx={{
						marginTop: 1,
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'wrap',
						columnGap: 1,
					}}
				>
					<Typography variant='body2' color='text.secondary'>
						{t('memberDuplication.previousFamilyMemberTips')}
					</Typography>
					<Button
						variant='text'
						size='small'
						sx={{ textTransform: 'none', minWidth: 0, padding: '0 4px' }}
						onClick={() => setPickerOpen(true)}
					>
						{t('memberDuplication.previousFamilyMemberInSystem')}
					</Button>
				</Box>
			)}
			<ClosedFamilyMemberPickerDialog
				open={pickerOpen}
				onClose={() => setPickerOpen(false)}
				onConfirm={(row) => {
					onChange(row);
					setPickerOpen(false);
				}}
			/>
		</>
	);
}
